<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Notificaciones"></navbar-dashboard>

      <div class="ew-view">
        <page-header title="Notificaciones"
          :subtitle="`Administradores con saldo igual o menor a ${formatCurrency(threshold)} MXN. Avísales por WhatsApp o correo.`">
          <button class="btn btn-outline-secondary" :class="{ active: showSettings }" @click="showSettings = !showSettings">
            <i class="bi bi-sliders mr-1"></i> {{ showSettings ? 'Ocultar ajustes' : 'Ajustes de alerta' }}
          </button>
        </page-header>

        <!-- Configuración: umbral y plantillas -->
        <div class="ew-section" v-if="showSettings">
          <h2 class="ew-section-title"><i class="bi bi-sliders text-primary"></i> Alerta de saldo bajo</h2>
          <p class="ew-section-subtitle">Define a partir de qué saldo se considera bajo y el mensaje que se envía.</p>
          <div>
            <div v-if="settingsError" class="alert alert-danger">{{ settingsError }}</div>
            <div v-if="settingsSaved" class="alert alert-success">Configuración guardada correctamente.</div>

            <div class="form-group">
              <label>Umbral de saldo bajo (MXN)</label>
              <input type="number" class="form-control" style="max-width: 220px;" v-model.number="settings.lowBalanceThreshold">
              <small class="text-muted">Los administradores con saldo igual o menor a este monto aparecerán en la lista.</small>
            </div>

            <div class="form-group">
              <label>Plantilla de correo</label>
              <textarea class="form-control" rows="3" v-model="settings.emailTemplate"></textarea>
              <small class="text-muted">Variables disponibles: <code v-text="'{{nombre}}'"></code> y <code v-text="'{{saldo}}'"></code></small>
            </div>

            <div class="form-group mb-0">
              <label>Plantilla de WhatsApp</label>
              <textarea class="form-control" rows="3" v-model="settings.whatsappTemplate"></textarea>
              <small class="text-muted">Variables disponibles: <code v-text="'{{nombre}}'"></code> y <code v-text="'{{saldo}}'"></code></small>
            </div>

            <div class="text-right mt-3">
              <button class="btn btn-primary" :disabled="savingSettings" @click="saveSettings">
                {{ savingSettings ? 'Guardando...' : 'Guardar configuración' }}
              </button>
            </div>
          </div>
        </div>

        <div class="ew-table-scroll">
            <div v-if="isLoading">
              <table class="table mb-0">
                <tbody>
                  <tr v-for="n in 5" :key="'sk-' + n">
                    <td v-for="c in 4" :key="'skc-' + c"><span class="ew-skeleton"></span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <empty-state v-else-if="loadError" error icon="bi bi-bell-slash" title="No pudimos cargar la lista"
              :text="loadError">
              <button class="btn btn-outline-secondary" @click="fetchLowBalanceAdmins">
                <i class="bi bi-arrow-clockwise mr-1"></i> Reintentar
              </button>
            </empty-state>

            <empty-state v-else-if="!users.length" icon="bi bi-check2-circle" title="Todo en orden"
              text="Ningún administrador tiene saldo bajo por el momento."></empty-state>

            <div v-else>
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Administrador</th>
                    <th>Teléfono</th>
                    <th class="ew-num">Saldo</th>
                    <th class="text-right">Notificar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user._id">
                    <td>
                      <div class="ew-party" style="max-width: 320px;">
                        <span class="ew-party-name">{{ [user.firstName, user.lastName].filter(Boolean).join(' ') | dash }}</span>
                        <span class="ew-party-place">{{ user.email | dash }}</span>
                      </div>
                    </td>
                    <td class="text-nowrap">
                      <span v-if="user.phone">{{ user.phone }}</span>
                      <span v-else class="text-muted">Sin teléfono</span>
                    </td>
                    <td class="ew-num">
                      <span class="badge" :class="user.balance <= 0 ? 'badge-danger' : 'badge-warning'">
                        {{ user.balance | money }}
                      </span>
                    </td>
                    <td class="text-right text-nowrap">
                      <a :href="whatsappLink(user)" target="_blank" rel="noopener"
                        class="btn btn-sm btn-outline-secondary mr-1"
                        :class="{ disabled: !user.phone }"
                        v-b-tooltip.hover.top="user.phone ? 'Enviar notificación de saldo bajo por WhatsApp' : 'Este usuario no tiene teléfono registrado'">
                        <i class="bi bi-whatsapp mr-1 ew-wa"></i> WhatsApp
                      </a>
                      <button class="btn btn-sm btn-outline-secondary" :disabled="sendingEmailFor === user._id"
                        @click="sendEmail(user)"
                        v-b-tooltip.hover.top="'Enviar notificación de saldo bajo por correo'">
                        <i class="bi bi-envelope mr-1"></i>
                        {{ sendingEmailFor === user._id ? 'Enviando...' : 'Correo' }}
                      </button>
                      <div v-if="emailFeedback[user._id]" class="small mt-1"
                        :class="emailFeedback[user._id].ok ? 'text-success' : 'text-danger'">
                        {{ emailFeedback[user._id].message }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import commons from '../commons';
import Navbar from './Header/Navbar';

export default {
  name: 'Notifications',
  components: {
    'navbar-dashboard': Navbar,
  },
  data() {
    return {
      users: [],
      threshold: 10000,
      isLoading: true,
      loadError: '',
      showSettings: false,
      settings: { lowBalanceThreshold: 10000, emailTemplate: '', whatsappTemplate: '' },
      savingSettings: false,
      settingsError: '',
      settingsSaved: false,
      sendingEmailFor: null,
      emailFeedback: {},
    };
  },
  created() {
    this.fetchSettings();
    this.fetchLowBalanceAdmins();
  },
  methods: {
    authHeaders() {
      return { headers: { 'x-access-token': localStorage.getItem('ew-user-token') } };
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
    fillTemplate(template, user) {
      return template
        .replace(/{{\s*nombre\s*}}/gi, user.firstName || '')
        .replace(/{{\s*saldo\s*}}/gi, this.formatCurrency(user.balance));
    },
    whatsappLink(user) {
      if (!user.phone) return '#';
      const digits = user.phone.replace(/\D/g, '');
      const phoneWithCountryCode = digits.length === 10 ? `52${digits}` : digits;
      const message = this.fillTemplate(this.settings.whatsappTemplate, user);
      return `https://wa.me/${phoneWithCountryCode}?text=${encodeURIComponent(message)}`;
    },
    fetchSettings() {
      axios
        .get(`${commons.apiURL}/api/v1/notifications/settings`, this.authHeaders())
        .then((response) => {
          this.settings = response.data;
        })
        .catch((error) => {
          console.error('Error al cargar configuración de notificaciones:', error);
        });
    },
    saveSettings() {
      this.savingSettings = true;
      this.settingsError = '';
      this.settingsSaved = false;
      axios
        .put(`${commons.apiURL}/api/v1/notifications/settings`, {
          lowBalanceThreshold: this.settings.lowBalanceThreshold,
          emailTemplate: this.settings.emailTemplate,
          whatsappTemplate: this.settings.whatsappTemplate,
        }, this.authHeaders())
        .then((response) => {
          this.settings = response.data;
          this.settingsSaved = true;
          this.fetchLowBalanceAdmins();
        })
        .catch((error) => {
          console.error('Error al guardar configuración de notificaciones:', error);
          this.settingsError = 'No se pudo guardar la configuración.';
        })
        .finally(() => {
          this.savingSettings = false;
        });
    },
    fetchLowBalanceAdmins() {
      this.isLoading = true;
      this.loadError = '';
      axios
        .get(`${commons.apiURL}/api/v1/notifications/low-balance-admins`, this.authHeaders())
        .then((response) => {
          this.users = response.data.users;
          this.threshold = response.data.threshold;
        })
        .catch((error) => {
          console.error('Error al cargar administradores con saldo bajo:', error);
          this.loadError = 'No se pudo cargar la lista. Intenta de nuevo.';
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    sendEmail(user) {
      this.sendingEmailFor = user._id;
      this.$set(this.emailFeedback, user._id, null);
      axios
        .post(`${commons.apiURL}/api/v1/notifications/email/${user._id}`, {
          balance: user.balance,
        }, this.authHeaders())
        .then(() => {
          this.$set(this.emailFeedback, user._id, { ok: true, message: 'Correo enviado.' });
        })
        .catch((error) => {
          console.error('Error al enviar correo de saldo bajo:', error);
          this.$set(this.emailFeedback, user._id, { ok: false, message: 'No se pudo enviar el correo.' });
        })
        .finally(() => {
          this.sendingEmailFor = null;
        });
    },
  },
};
</script>

<style scoped>
.ew-wa {
  color: #15803d;
}
</style>



// WEBPACK FOOTER //
// src/components/Notifications.vue